import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AgencyDashboardService } from '@/services/agencyDashboard.service';
import {
    AgencyDashboardPeriod,
    ClientDetailResult,
    ClientKey,
} from '@/integrations/supabase/rpc-types';

interface UseClientDetailParams {
    /** `null` = pannello chiuso: nessuna chiamata parte. */
    clientKey: ClientKey | null;
    period: AgencyDashboardPeriod;
    /** false = storico completo, true = solo il periodo selezionato in pagina. */
    periodOnly: boolean;
}

interface UseClientDetailResult {
    data: ClientDetailResult | null;
    loading: boolean;
    error: string | null;
    /** La chiave esiste per il chiamante ma il database non la vede: scheda non aperta. */
    notFound: boolean;
    refresh: () => Promise<void>;
}

/**
 * Scheda del singolo cliente, caricata all'apertura del pannello.
 *
 * Non si riusa la riga gia' in mano alla lista: la scheda mostra lo storico completo,
 * mentre la riga e' filtrata dal periodo. Sono due insiemi diversi e sovrapporli darebbe
 * totali che non tornano.
 */
export function useClientDetail({
    clientKey,
    period,
    periodOnly,
}: UseClientDetailParams): UseClientDetailResult {
    const [data, setData] = useState<ClientDetailResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notFound, setNotFound] = useState(false);
    const { t } = useI18n();

    const fetchData = useCallback(async () => {
        if (!clientKey) {
            setData(null);
            setNotFound(false);
            return;
        }

        setLoading(true);
        setError(null);
        setNotFound(false);

        try {
            const result = await AgencyDashboardService.getClientDetail(
                clientKey,
                period,
                periodOnly
            );
            setData(result);
            setNotFound(result === null);
        } catch {
            const msg = String(t('errors.loadFailed'));
            setError(msg);
            toast({
                title: String(t('common.error')),
                description: msg,
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientKey, period, periodOnly]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { data, loading, error, notFound, refresh: fetchData };
}

export default useClientDetail;
